import {
	is1pProductKey,
	ProductKeys,
	ProductKeys3P,
	products1P,
} from '../../../../common/constants/products';
import type { ThirdPartyConfigsWithAPIResponse } from '../../../../common/constants/schemas/3p-config';
import {
	type GhostTextDataV2,
	type GhostTextRotation,
} from '../../../../common/utils/quick-find/ghost-text';

import { ghostTextMessages } from './ghost-text-messages';
import { selectGhostText } from './select-ghost-text';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { selectGhostText } from './select-ghost-text';

// TODO: to be used in second experiment https://hello.atlassian.net/wiki/pages/resumedraft.action?draftId=6192988799
/**
 * Generate ghost text rotations for SAIN cohort (AI-powered question suggestions)
 */
// const calculateSainGhostTextRotations = (
// 	formatMessage: (message: any, values?: any) => string,
// ): string[] => {
// 	return [
// 		formatMessage(ghostTextMessages.sainQuestionTimeOff),
// 		formatMessage(ghostTextMessages.sainQuestionResetPassword),
// 		formatMessage(ghostTextMessages.sainQuestionNotifications),
// 		formatMessage(ghostTextMessages.sainQuestionITSupport),
// 		formatMessage(ghostTextMessages.sainQuestionMeetingRoom),
// 		formatMessage(ghostTextMessages.sainQuestionBenefits),
// 	];
// };

/**
 * Generate ghost text rotations for 3P cohort (based on available products)
 * Returns ordered list: combined texts -> 3P specific texts (sorted by hash)
 */
const calculate3PGhostTextRotations = ({
	products,
	thirdPartyConfigs,
	primaryProduct,
	formatMessage,
}: {
	products: ProductKeys[];
	thirdPartyConfigs: Partial<Record<string, ThirdPartyConfigsWithAPIResponse>>;
	primaryProduct?: string;
	formatMessage: (message: any, values?: any) => string;
}): GhostTextRotation[] => {
	const SUPPORTED_3P_MESSAGES = new Map<string, any>([
		[ProductKeys3P.GoogleDrive, ghostTextMessages.googleDrivePlaceholder],
		[ProductKeys3P.Sharepoint, ghostTextMessages.sharepointPlaceholder],
		[ProductKeys3P.OneDrive, ghostTextMessages.onedrivePlaceholder],
		[ProductKeys3P.Teams, ghostTextMessages.teamsPlaceholder],
		[ProductKeys3P.Slack, ghostTextMessages.slackPlaceholder],
		[ProductKeys3P.Github, ghostTextMessages.githubPlaceholder],
	]);

	const currentAppName =
		primaryProduct && is1pProductKey(primaryProduct)
			? products1P[primaryProduct]?.displayName
			: undefined;

	const available1PExcludePrimary = Array.from(
		new Set(
			products.filter(is1pProductKey).map((productKey) => {
				// Consolidate Jira variants to base Jira for ghost text
				if (
					productKey === ProductKeys.JiraProductDiscovery ||
					productKey === ProductKeys.JiraServiceManagement
				) {
					return ProductKeys.Jira;
				}
				return productKey;
			}),
		),
	).filter((productKey) => productKey !== primaryProduct);

	const available3PProducts = Object.keys(thirdPartyConfigs || {}).filter(
		(key) =>
			thirdPartyConfigs?.[key] !== undefined &&
			!thirdPartyConfigs?.[key]?.userNeedsOAuth &&
			!key.toLowerCase().startsWith('forge-connector-') &&
			!key.toLowerCase().includes(ProductKeys3P.AtlassianWebCrawler) &&
			!key.toLowerCase().endsWith('-dc'),
	);

	const textRotations: GhostTextRotation[] = [];

	if (currentAppName) {
		if (available3PProducts.length >= 1 && available1PExcludePrimary.length >= 1) {
			const top1P = products1P[available1PExcludePrimary[0]].displayName;
			const top3P = thirdPartyConfigs?.[available3PProducts[0]]?.displayName;

			textRotations.push({
				id: ghostTextMessages.mixedAppsPlaceholder.id,
				text: formatMessage(ghostTextMessages.mixedAppsPlaceholder, {
					currentApp: currentAppName,
					otherFirstPartyApp: top1P,
					thirdPartyApp: top3P,
				}),
			});
		}

		if (available3PProducts.length >= 2) {
			const top3P1 = thirdPartyConfigs?.[available3PProducts[0]]?.displayName;
			const top3P2 = thirdPartyConfigs?.[available3PProducts[1]]?.displayName;

			textRotations.push({
				id: ghostTextMessages.multipleThirdPartyPlaceholder.id,
				text: formatMessage(ghostTextMessages.multipleThirdPartyPlaceholder, {
					firstPartyApp: currentAppName,
					thirdPartyApp1: top3P1,
					thirdPartyApp2: top3P2,
				}),
			});
		}

		if (available3PProducts.length === 1 && available1PExcludePrimary.length === 0) {
			const top3P = thirdPartyConfigs?.[available3PProducts[0]]?.displayName;

			textRotations.push({
				id: ghostTextMessages.twoAppPlaceholder.id,
				text: formatMessage(ghostTextMessages.twoAppPlaceholder, {
					firstPartyApp: currentAppName,
					thirdPartyApp1: top3P,
				}),
			});
		}
	}

	for (const productKey of available3PProducts) {
		const message = SUPPORTED_3P_MESSAGES.get(productKey);
		if (message) {
			textRotations.push({
				id: message.id,
				text: formatMessage(message),
			});
		}
	}

	return textRotations;
};

/**
 * Calculate all ghost text rotations based on experiment cohort.
 */
export const calculateGhostTextRotations = ({
	products,
	thirdPartyConfigs,
	primaryProduct,
	formatMessage,
}: {
	products: ProductKeys[];
	thirdPartyConfigs: Partial<Record<string, ThirdPartyConfigsWithAPIResponse>>;
	primaryProduct?: string;
	formatMessage: (message: any, values?: any) => string;
}): GhostTextRotation[] => {
	const rotations = calculate3PGhostTextRotations({
		products,
		thirdPartyConfigs,
		primaryProduct,
		formatMessage,
	}).filter((r) => r.text.length <= 85);

	rotations.push({
		id: ghostTextMessages.default3PPlaceholder.id,
		text: formatMessage(ghostTextMessages.default3PPlaceholder),
	});

	return rotations;
};

/**
 * Generate ghost text data based on experiment cohort and current state.
 * Calculates rotations, selects appropriate text, and returns the new GhostTextDataV2.
 *
 * @returns GhostTextDataV2 object
 */
export const generateGhostTextData = ({
	products,
	thirdPartyConfigs,
	locale,
	primaryProduct,
	formatMessage,
	existingGhostTextData,
}: {
	products: ProductKeys[];
	thirdPartyConfigs: Partial<Record<string, ThirdPartyConfigsWithAPIResponse>>;
	locale: string;
	primaryProduct?: string;
	formatMessage: (message: any, values?: any) => string;
	existingGhostTextData?: GhostTextDataV2;
}): GhostTextDataV2 => {
	// Get all text rotations
	const rotations = calculateGhostTextRotations({
		products,
		thirdPartyConfigs,
		primaryProduct,
		formatMessage,
	});

	// Select appropriate text based on current state
	const { id, text, occurrence } = selectGhostText(rotations, locale, existingGhostTextData);

	return {
		id,
		locale,
		text,
		occurrence,
	};
};

/**
 * Check if a serialized ghost text value is considered "empty"
 */
const isEmptyGhostText = (value: string | null | undefined): boolean => {
	return value === '{}' || value === '' || value === null || value === undefined;
};

/**
 * Generate and serialize ghost text data for persistence.
 * Returns null if the update should be rejected (to prevent overwriting valid data with empty).
 *
 * @returns Serialized ghost text data, or null if update should be rejected
 */
export const generateSerializedGhostText = ({
	serve3PGhostText,
	lastPersisted,
	products,
	thirdPartyConfigs,
	locale,
	primaryProduct,
	formatMessage,
	existingGhostTextData,
}: {
	serve3PGhostText: boolean;
	lastPersisted: string;
	products: ProductKeys[];
	thirdPartyConfigs: Partial<Record<string, ThirdPartyConfigsWithAPIResponse>>;
	locale: string;
	primaryProduct?: string;
	formatMessage: (message: any, values?: any) => string;
	existingGhostTextData?: GhostTextDataV2;
}): string | null => {
	const newGhostTextData = serve3PGhostText
		? generateGhostTextData({
				products,
				thirdPartyConfigs,
				locale,
				primaryProduct,
				formatMessage,
				existingGhostTextData,
			})
		: {};

	const serialized = JSON.stringify(newGhostTextData);

	// Reject transition from non-empty to empty (don't overwrite valid data)
	// Accept transition from empty to non-empty (allow valid data to be set)
	const isNewEmpty = isEmptyGhostText(serialized);
	const wasLastEmpty = isEmptyGhostText(lastPersisted);

	if (!wasLastEmpty && isNewEmpty) {
		// Don't overwrite valid ghost text with empty
		return null;
	}

	return serialized;
};
